import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para gestión de facturas/ventas
 */
export const facturasService = {
  // Obtener lista de facturas
  getFacturas: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener factura por ID
  getFacturaById: async (id) => {
    try {
      const response = await apiClient.get(`/invoices/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Crear nueva factura
  createFactura: async (data) => {
    try {
      console.log('Creando factura con datos:', data);
      const response = await apiClient.post('/invoices', data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en createFactura:', error);
      return handleApiError(error);
    }
  },

  // Actualizar factura
  updateFactura: async (id, data) => {
    try {
      console.log('Actualizando factura con datos:', data);
      const response = await apiClient.put(`/invoices/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateFactura:', error);
      return handleApiError(error);
    }
  },

  // Eliminar factura
  deleteFactura: async (id) => {
    try {
      const response = await apiClient.delete(`/invoices/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Registrar pago de factura
  markFacturaAsPaid: async (id, paymentData = {}) => {
    try {
      // Mapear métodos de pago del frontend (español) a la API (enum)
      const methodMap = {
        'efectivo': 'cash',
        'transferencia': 'bank_transfer',
        'tarjeta_credito': 'credit_card',
        'tarjeta_debito': 'debit_card',
        'cheque': 'check',
        'electronico': 'electronic',
        'otro': 'other'
      };

      const rawMethod = paymentData.payment_method || 'cash';
      const mappedMethod = methodMap[rawMethod] || rawMethod;

      // Mapear campos del frontend a los que espera la API (InvoicePaymentCreate)
      const apiPaymentData = {
        amount: paymentData.payment_amount || paymentData.amount,
        payment_method: mappedMethod,
        payment_date: paymentData.payment_date || new Date().toISOString().split('T')[0],
        reference_number: paymentData.payment_reference || paymentData.reference_number || null,
        notes: paymentData.notes || null
      };

      const response = await apiClient.post(`/invoices/${id}/payment`, apiPaymentData);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error recording payment:', error);
      return handleApiError(error);
    }
  },

  // Obtener estadísticas de facturas
  getEstadisticas: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/statistics', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Generar PDF de factura
  generatePDF: async (id) => {
    try {
      const response = await apiClient.get(`/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener facturas pendientes
  getFacturasPendientes: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/pending', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener facturas vencidas
  getFacturasVencidas: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/overdue', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Alias para generatePDF (compatibilidad)
  generateInvoicePDF: async (id) => {
    return facturasService.generatePDF(id);
  },

  // Generar lista de empaque (packing list)
  generatePackingList: async (id, options = {}) => {
    try {
      const response = await apiClient.get(`/invoices/${id}/packing-list`, {
        params: options,
        responseType: 'blob'
      });
      return {
        success: true,
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Enviar recordatorio de pago
  sendPaymentReminder: async (id, reminderData = {}) => {
    try {
      const response = await apiClient.post(`/invoices/reminders/send`, {
        invoice_ids: [id],
        ...reminderData
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener facturas sin pagar
  getUnpaidInvoices: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/unpaid', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener facturas pagadas
  getPaidInvoices: async (params = {}) => {
    try {
      const response = await apiClient.get('/invoices/paid', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Obtener facturas vencidas (alias)
  getOverdueInvoices: async (params = {}) => {
    return facturasService.getFacturasVencidas(params);
  },

  // Buscar facturas
  searchFacturas: async (query, params = {}) => {
    try {
      const response = await apiClient.get('/invoices/search', {
        params: { q: query, ...params }
      });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
};

/**
 * Utilidades para manejo de facturas
 */
export const invoiceUtils = {
  // Formatear moneda
  formatCurrency: (amount, currency = 'COP') => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  },

  // Formatear fecha
  formatDate: (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

export default facturasService;